/**
 * 회기 진행 1단계: 두 기기 전사가 들어왔는지, 문제가 없는지 바로 알려준다 (처리 전에).
 * - 기기별로 들어왔는지·길이(분)
 * - 같은 파일을 두 번 넣었는지, 두 전사가 같은 상담인지, 한쪽이 일찍 끝났는지
 */
import { detectTranscript } from './detect';

export type DeviceInfo = { present: boolean; minutes: number | null };
export type InputCheck = { galaxy: DeviceInfo; iphone: DeviceInfo; notes: { level: 'ok' | 'warn'; text: string }[] };

const info = (text: string): DeviceInfo => {
  if (!text.trim()) return { present: false, minutes: null };
  const d = detectTranscript(text);
  return { present: true, minutes: d.lastTimestampMs === null ? null : Math.max(1, Math.round(d.lastTimestampMs / 60000)) };
};

/** 이어진 두 낱말 묶음 집합 (낱말 하나보다 구별력이 높다): 앞부분만 본다 */
function pairs(text: string, limit = 4000): Set<string> {
  const ws = text
    .replace(/(발화자|참석자|화자)\s*\d+/g, ' ')
    .replace(/\d{1,2}:\d{2}(:\d{2})?/g, ' ')
    .split(/[^0-9A-Za-z가-힣]+/)
    .filter((w) => w.length >= 2)
    .slice(0, limit);
  const out = new Set<string>();
  for (let k = 1; k < ws.length; k++) out.add(ws[k - 1] + ' ' + ws[k]);
  return out;
}

export function checkTranscriptPair(galaxy: string, iphone: string): InputCheck {
  const g = info(galaxy);
  const i = info(iphone);
  const notes: InputCheck['notes'] = [];
  if (g.present && i.present) {
    if (galaxy.replace(/\s+/g, '') === iphone.replace(/\s+/g, '')) {
      notes.push({ level: 'warn', text: '같은 파일이 두 번 들어왔습니다. 다른 기기의 전사를 넣어 주세요.' });
    } else {
      const a = pairs(galaxy);
      const b = pairs(iphone);
      let common = 0;
      for (const w of a) if (b.has(w)) common++;
      const overlap = common / Math.max(1, Math.min(a.size, b.size));
      notes.push(
        overlap >= 0.15
          ? { level: 'ok', text: '두 전사의 내용이 서로 맞습니다 (같은 상담).' }
          : { level: 'warn', text: '두 전사의 내용이 많이 다릅니다. 같은 상담의 녹음인지 확인해 주세요.' },
      );
    }
    if (g.minutes && i.minutes && Math.abs(g.minutes - i.minutes) >= 2) {
      const early = g.minutes < i.minutes ? '갤럭시' : '아이폰';
      notes.push({
        level: 'warn',
        text: `${early} 녹음이 ${Math.abs(g.minutes - i.minutes)}분 일찍 끝났습니다. 끝부분은 다른 쪽 전사로 이어 씁니다.`,
      });
    }
  }
  if (g.present && detectTranscript(galaxy).kind === 'iphone')
    notes.push({ level: 'warn', text: '갤럭시 칸의 전사가 아이폰(다글로) 형식처럼 보입니다.' });
  if (i.present && detectTranscript(iphone).kind === 'galaxy')
    notes.push({ level: 'warn', text: '아이폰 칸의 전사가 갤럭시 형식처럼 보입니다.' });
  return { galaxy: g, iphone: i, notes };
}
